import { Controller } from "@hotwired/stimulus";
import Toastify from "toastify-js";

export default class extends Controller {
  static targets = ["status", "downloadLink", "exportsList", "exportItem", "nameInput"];
  static values = {
    exportId: String,
    campId: String,
    pollingInterval: { type: Number, default: 3000 },
    checkUrl: String
  };
  
  connect() {
    if (this.hasExportIdValue) {
      this.startPolling();
    }
    
    this.loadExistingExports();
  }
  
  disconnect() {
    this.stopPolling();
  }
  
  startPolling() {
    this.pollingId = setInterval(() => {
      this.checkExportStatus();
    }, this.pollingIntervalValue);
  }
  
  stopPolling() {
    if (this.pollingId) {
      clearInterval(this.pollingId);
    }
  }
  
  async checkExportStatus() {
    try {
      const response = await fetch(this.checkUrlValue);
      const data = await response.json();
      
      this.updateStatus(data);
      
      if (data.status === "completed" || data.status === "failed") {
        this.stopPolling();
        this.loadExistingExports();
      }
    } catch (error) {
      console.error("Error checking export status:", error);
    }
  }
  
  updateStatus(data) {
    if (this.hasStatusTarget) {
      let statusText = "";
      let statusClass = "";
      
      switch (data.status) {
        case "pending":
          statusText = "Pending";
          statusClass = "pending";
          break;
        case "processing":
          statusText = "Processing...";
          statusClass = "processing";
          break;
        case "completed":
          statusText = "Completed";
          statusClass = "completed";
          this.showDownloadLink(data.download_url);
          this.showSuccessToast();
          break;
        case "failed":
          statusText = `Failed: ${data.error_message}`;
          statusClass = "failed";
          this.showErrorToast(data.error_message);
          break;
      }
      
      this.statusTarget.textContent = statusText;
      this.statusTarget.className = `export-status ${statusClass}`;
    }
  }
  
  showDownloadLink(url) {
    if (this.hasDownloadLinkTarget && url) {
      this.downloadLinkTarget.href = url;
      this.downloadLinkTarget.classList.remove("hidden");
    }
  }
  
  showSuccessToast() {
    Toastify({
      text: "CSV export completed successfully!",
      duration: 4000,
      close: true,
      position: "right",
      className: "success",
      stopOnFocus: true
    }).showToast();
  }
  
  showErrorToast(message) {
    Toastify({
      text: `CSV export failed: ${message}`,
      duration: 4000,
      close: true,
      position: "right",
      className: "error",
      stopOnFocus: true
    }).showToast();
  }
  
  async loadExistingExports() {
    if (!this.hasExportsListTarget) return;
    
    try {
      const response = await fetch(`/admin/camps/${this.campIdValue}/csv_exports.json`);
      const exports = await response.json();
      
      this.exportsListTarget.innerHTML = "";
      
      exports.forEach(exportItem => {
        const item = this.createExportItem(exportItem);
        this.exportsListTarget.appendChild(item);
      });
    } catch (error) {
      console.error("Error loading exports:", error);
    }
  }
  
  createExportItem(exportData) {
    const template = document.createElement("template");
    
    let statusClass = "";
    switch (exportData.status) {
      case "pending": statusClass = "pending"; break;
      case "processing": statusClass = "processing"; break;
      case "completed": statusClass = "completed"; break;
      case "failed": statusClass = "failed"; break;
    }
    
    template.innerHTML = `
      <div class="export-item" data-csv-export-status-target="exportItem" data-export-id="${exportData.id}">
        <div class="export-item-header">
          <span class="export-name">${exportData.name}</span>
          <span class="export-status ${statusClass}">${exportData.status}</span>
        </div>
        <div class="export-item-details">
          <span class="export-date">${new Date(exportData.created_at).toLocaleString()}</span>
          ${exportData.status === "completed" ? 
            `<a href="${exportData.download_url}" class="download-link">Download</a>` : 
            ''}
          ${exportData.status === "failed" ? 
            `<span class="error-message">${exportData.error_message}</span>` : 
            ''}
        </div>
        <div class="export-item-actions">
          <input type="text" class="name-input" data-csv-export-status-target="nameInput" value="${exportData.name}">
          <button data-action="click->csv-export-status#renameExport" data-export-id="${exportData.id}">Rename</button>
          <button data-action="click->csv-export-status#repeatExport" data-export-id="${exportData.id}">Repeat Export</button>
        </div>
      </div>
    `;
    
    return template.content.firstElementChild;
  }
  
  async renameExport(event) {
    const exportId = event.currentTarget.dataset.exportId;
    const nameInput = event.currentTarget.closest(".export-item-actions").querySelector(".name-input");
    const newName = nameInput.value.trim();
    
    if (!newName) return;
    
    try {
      const response = await fetch(`/admin/camps/${this.campIdValue}/rename_csv_export`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content
        },
        body: JSON.stringify({
          csv_export_id: exportId,
          name: newName
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        Toastify({
          text: "Export renamed successfully",
          duration: 3000,
          close: true,
          position: "right",
          className: "success",
          stopOnFocus: true
        }).showToast();
        
        this.loadExistingExports();
      } else {
        Toastify({
          text: `Failed to rename export: ${data.errors.join(", ")}`,
          duration: 3000,
          close: true,
          position: "right",
          className: "error",
          stopOnFocus: true
        }).showToast();
      }
    } catch (error) {
      console.error("Error renaming export:", error);
    }
  }
  
  async repeatExport(event) {
    const exportId = event.currentTarget.dataset.exportId;
    
    try {
      const response = await fetch(`/admin/camps/${this.campIdValue}/repeat_csv_export`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content
        },
        body: JSON.stringify({
          csv_export_id: exportId
        })
      });
      
      const data = await response.json();
      
      if (data.id) {
        this.exportIdValue = data.id;
        this.startPolling();
        
        Toastify({
          text: "Export started",
          duration: 3000,
          close: true,
          position: "right",
          className: "success",
          stopOnFocus: true
        }).showToast();
        
        this.loadExistingExports();
      }
    } catch (error) {
      console.error("Error repeating export:", error);
    }
  }
}
